import { org, siteUrl } from "@/data/site";
import { absoluteUrl } from "@/lib/site-url";
import type { Metadata, OgImage } from "@/types/metadata";

export const defaultOgImage: OgImage = {
  url: "/og-default.jpg",
  width: 1200,
  height: 630,
  alt: `${org.legalName}: ${org.positioning}`,
};

/**
 * Builds per-route metadata: unique title, unique description, canonical URL and
 * Open Graph / Twitter cards. Every public route calls this.
 */
export function buildMetadata({
  title,
  description,
  path,
  type = "website",
  publishedTime,
  titleIsComplete = false,
  image,
  canonical,
  noindex = false,
}: {
  title: string;
  description: string;
  path: string;
  type?: "website" | "article";
  publishedTime?: string;
  /**
   * Suppresses the " | HISCUS Nepal" suffix the head renderer normally appends.
   *
   * Only the homepage sets it: its title already carries the organisation's
   * full legal name, so the suffix produced the name twice in one tab.
   */
  titleIsComplete?: boolean;
  /** The picture a shared link shows. The site default when a page has none of its own. */
  image?: OgImage;
  /**
   * Where search engines should treat this page as living. Empty is the page's
   * own address, which is right almost every time.
   */
  canonical?: string;
  /** Asks search engines not to index this page. */
  noindex?: boolean;
}): Metadata {
  const url = canonical ? absoluteUrl(canonical) : `${siteUrl}${path === "/" ? "" : path}`;
  const shareImage = image ?? defaultOgImage;

  return {
    title,
    titleIsComplete,
    description,
    alternates: { canonical: url },
    ...(noindex ? { robots: "noindex, follow" } : {}),
    openGraph: {
      title: `${title} | ${org.shortName}`,
      description,
      url,
      siteName: org.legalName,
      locale: "en_NP",
      type,
      images: [shareImage],
      ...(publishedTime ? { publishedTime } : {}),
    },
    twitter: {
      card: "summary_large_image",
      title: `${title} | ${org.shortName}`,
      description,
      images: [shareImage.url],
    },
  };
}
